import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Icon, Label } from 'semantic-ui-react';
import styled from 'styled-components';
import UserMenu from './UserMenu';
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin';
import { useFeatureFlag } from '../featureFlags/FeatureFlagContext';
import { FeatureToogles } from '../../shared';
import { Sparkles, Inbox, KeyRound, Cpu } from 'lucide-react';
const StyledMenu = styled(Menu) `
  background-color: white !important;
  margin: 0 !important;
  border-radius: 0 !important;
  border: none !important;
  border-bottom: 1px solid #f0f0f0 !important;
  height: 45px;
  padding: 0 3rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  #root & a {
    font-size: 1.0rem;
  }
`;
const LogoContainer = styled.div.attrs({
    className: 'navigation-logo-container'
}) `
  display: flex;
  align-items: center;
  height: 100%;
  margin-right: 1.5rem;
  
  img {
    height: 30px;
    width: auto;
  }
`;
const MenuItems = styled.div.attrs({
    className: 'navigation-menu-items'
}) `
  display: flex;
  justify-content: center;
  flex: 1;
  gap: 2rem;
  height: 100%;
  align-items: center;
  margin-left: 3rem;
`;
const MenuItem = styled(Menu.Item) `
  &&&& {
    display: flex;
    align-items: center;
    color: var(--primary-color) !important;
    opacity: ${props => props.$active ? 1 : 0.7};
    font-weight: ${props => props.$active ? 500 : 400};
    transition: all 0.2s ease;
    padding: 0 0.5rem;

    &:hover {
      opacity: 1;
    }

    i.icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      opacity: 0.7;
      margin-right: 0.5rem;
      height: 16px;
      width: auto;
      font-size: 16px;
    }

    svg {
      flex-shrink: 0;
    }
  }
`;
const VersionNumber = styled.div.attrs({
    className: 'navigation-version-number'
}) `
  color: var(--primary-color);
  opacity: 0.5;
  font-size: 1.0rem;
  display: flex;
  align-items: center;
  font-weight: 500;
  letter-spacing: 0.02em;
`;
function useImpersonation() {
    const location = useLocation();
    return useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            companyId: params.get('companyId'),
            companyName: params.get('companyName'),
        };
    }, [location.search]);
}
export const NavigationMenu = () => {
    const location = useLocation();
    const isSuperAdmin = useIsSuperAdmin();
    const { companyId: impersonatedCompanyId, companyName: impersonatedCompanyName } = useImpersonation();
    const isFormsActive = location.pathname === '/' || location.pathname.startsWith('/forms');
    const isAnalyticsActive = location.pathname.startsWith('/analytics');
    const isSubmissionsActive = location.pathname.startsWith('/submissions');
    const isIntegrationsActive = location.pathname.startsWith('/integrations');
    const isNewFeaturesActive = location.pathname.startsWith('/new-features');
    const isTestUiActive = location.pathname.startsWith('/test-ui');
    const isDevDocsActive = location.pathname.startsWith('/dev-docs');
    const isFeatureFlagsActive = location.pathname.startsWith('/feature-flags');
    const isFluidAdminActive = location.pathname.startsWith('/fluid-admin');
    const isApiTokensActive = location.pathname.startsWith('/api-tokens');
    const isComponentsActive = location.pathname.startsWith('/components');
    const isExamplesActive = location.pathname.startsWith('/examples');
    const { enabled: isGDriveIntegrationEnabled } = useFeatureFlag(FeatureToogles.GDriveIntegration);
    const { enabled: isIntegrationsEnabled } = useFeatureFlag(FeatureToogles.Integrations);
    const { enabled: isDevDocsEnabled } = useFeatureFlag(FeatureToogles.DevDocs);
    const { enabled: isApiTokensEnabled } = useFeatureFlag(FeatureToogles.ApiTokens);
    return (React.createElement(StyledMenu, null,
        React.createElement(Menu.Item, { style: { padding: 0 } },
            React.createElement(LogoContainer, null,
                React.createElement(Link, { to: "/" },
                    React.createElement("img", { src: "/fluid-form-logo.png", alt: "Fluid Form Logo" })))),
        React.createElement(MenuItems, null,
            React.createElement(MenuItem, { as: Link, to: "/", "$active": isFormsActive, className: isFormsActive ? 'active' : undefined },
                React.createElement(Icon, { name: "wpforms" }),
                "Forms"),
            React.createElement(MenuItem, { as: Link, to: "/submissions", "$active": isSubmissionsActive, className: isSubmissionsActive ? 'active' : undefined },
                React.createElement(Inbox, { size: 16, style: { marginRight: '0.5rem' } }),
                "Submissions"),
            React.createElement(MenuItem, { as: Link, to: "/components", "$active": isComponentsActive, className: isComponentsActive ? 'active' : undefined },
                React.createElement(Cpu, { size: 16, style: { marginRight: '0.5rem' } }),
                "Components"),
            React.createElement(MenuItem, { as: Link, to: "/analytics", "$active": isAnalyticsActive, className: isAnalyticsActive ? 'active' : undefined },
                React.createElement(Icon, { name: "chart bar" }),
                "Analytics"),
            isIntegrationsEnabled && React.createElement(MenuItem, { as: Link, to: "/integrations", "$active": isIntegrationsActive, className: isIntegrationsActive ? 'active' : undefined },
                React.createElement(Icon, { name: "plug" }),
                "Integrations"),
            React.createElement(MenuItem, { as: Link, to: "/new-features", "$active": isNewFeaturesActive, className: isNewFeaturesActive ? 'active' : undefined },
                React.createElement(Sparkles, { size: 16, style: { marginRight: '0.5rem' } }),
                "New Features"),
            isApiTokensEnabled && React.createElement(MenuItem, { as: Link, to: "/api-tokens", "$active": isApiTokensActive, className: isApiTokensActive ? 'active' : undefined },
                React.createElement(KeyRound, { size: 16, style: { marginRight: '0.5rem' } }),
                "API Tokens"),
            isDevDocsEnabled && React.createElement(MenuItem, { as: Link, to: "/examples", "$active": isExamplesActive, className: isExamplesActive ? 'active' : undefined },
                React.createElement(Icon, { name: "book" }),
                "Dev Docs"),
            isSuperAdmin && (React.createElement(MenuItem, { as: Link, to: "/feature-flags", "$active": isFeatureFlagsActive, className: isFeatureFlagsActive ? 'active' : undefined },
                React.createElement(Icon, { name: "setting" }),
                "Feature Flags")),
            isSuperAdmin && (React.createElement(MenuItem, { as: Link, to: "/fluid-admin", "$active": isFluidAdminActive, className: isFluidAdminActive ? 'active' : undefined },
                React.createElement(Icon, { name: "dashboard" }),
                "Fluid Admin"))),
        isSuperAdmin && impersonatedCompanyId && (React.createElement(Label, { color: "orange", style: { marginRight: '1rem', whiteSpace: 'nowrap' } },
            React.createElement(Icon, { name: "spy" }),
            "Impersonating ",
            impersonatedCompanyName || impersonatedCompanyId)),
        React.createElement(UserMenu, null)));
};
